import { apiClient } from './client';
import type {
  CreateTryOnJobRequest,
  TryOnAnalyticsEvent,
  TryOnFraming,
  TryOnJobResponse,
  TryOnProductDetail,
  TryOnUploadResponse,
} from '../../types/tryOn';

export const TRY_ON_ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const TRY_ON_MAX_IMAGE_BYTES = 8 * 1024 * 1024;

/**
 * Try-on är knutet till chattsessionen: backend läser inloggningen ur den
 * (resolveVerificationScope === 'login'). Sessionen ägs av chatt-appen, inte
 * av produktkorten, så den sätts en gång här i stället för att trås genom
 * ChatMessage → ProductSlider → TryOnModal, som ingen av dem behöver känna.
 */
let currentChatSessionId: string | null = null;

export function setTryOnChatSession(sessionId: string | null): void {
  currentChatSessionId = sessionId;
}

function sessionHeaders(): Record<string, string> {
  return currentChatSessionId ? { 'x-chat-session-id': currentChatSessionId } : {};
}

export async function uploadTryOnImage(
  file: File,
  framing: TryOnFraming = 'full',
): Promise<TryOnUploadResponse> {
  const formData = new FormData();
  formData.append('framing', framing);
  formData.append('image', file);
  const { data } = await apiClient.post<TryOnUploadResponse>('/try-on/uploads', formData, {
    headers: { 'Content-Type': 'multipart/form-data', ...sessionHeaders() },
  });
  return data;
}

export async function createTryOnJob(payload: CreateTryOnJobRequest): Promise<TryOnJobResponse> {
  const { data } = await apiClient.post<TryOnJobResponse>('/try-on/jobs', payload, {
    headers: sessionHeaders(),
  });
  return data;
}

export async function getTryOnJob(jobId: string): Promise<TryOnJobResponse> {
  const { data } = await apiClient.get<TryOnJobResponse>(`/try-on/jobs/${jobId}`, {
    headers: sessionHeaders(),
  });
  return data;
}

export async function deleteTryOnJob(jobId: string): Promise<void> {
  await apiClient.delete(`/try-on/jobs/${jobId}`, { headers: sessionHeaders() });
}

export async function deleteTryOnUpload(uploadId: string): Promise<void> {
  await apiClient.delete(`/try-on/uploads/${encodeURIComponent(uploadId)}`, {
    headers: sessionHeaders(),
  });
}

export async function trackTryOnEvent(event: TryOnAnalyticsEvent): Promise<void> {
  await apiClient.post('/try-on/events', event, { headers: sessionHeaders() });
}

/** Fullständiga produktdetaljer för "Produktbeskrivning" i resultatvyn. */
export async function getCatalogProductDetail(
  productId: string,
  lang?: string,
): Promise<TryOnProductDetail | null> {
  // Katalogfeeden bär ETT språk per produkt och det varierar — backend
  // översätter när lang skickas med, cachat per produkt+språk.
  const { data } = await apiClient.get<{ product: TryOnProductDetail | null }>(
    `/catalog/products/${encodeURIComponent(productId)}`,
    { params: lang ? { lang } : undefined, headers: sessionHeaders() },
  );
  return data.product ?? null;
}
